import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import sanitizeFileName from "helpers/sanitizeFileName";
import NoImage from "../../../assets/images/image_not_found.webp";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface BannerItem {
  id?: number;
  geo: string[];
  title: string;
  image?: string;
  isActive: boolean | number;
  banners?: BannerListItem[];
}
export interface BannerBunchPayload {
  ids: number[];
  isActive: number;
}

export interface BannerListItem {
  id: number;
  geo: string;
  isActive: false;
  ctaType: string;
  cta?: {
    actionType: string;
    instanceId?: number;
    slug?: string;
    providerSlug?: string;
  };
  imageBig: string;
  imageSmall: string;
  texts: [
    {
      locale: string;
      title: string;
      content: string;
      cta: string;
      note: string;
    }[],
  ];
}

export interface BannerPayload {
  imageBig: string | File;
  imageSmall: string | File;
  geo: string;
  ctaType: string;
  ctaProviderSlug?: string;
  // anchor: string;
  ctaActionType?: string;
  ctaInstanceId?: number;
  ctaSlug?: string;

  bannerGroupId: string | number;
  id?: string;
  innerId?: string;
  locales: {
    locale: string;
    title: string;
    content: string;
    cta: string;
    note: string;
  }[];
}
interface BannersState {
  list: BannerItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  isFetching: boolean;
  selectedItems: number[];
  payloadGroup?: BannerItem;
  payload?: BannerPayload[];
  ctaTargets: {
    label: string;
    value: number;
    slug?: string;
    img?: string;
    providerSlug?: string;
  }[];
  isCtaLoading?: boolean;
  processingItemId?: number;
}
interface GetListPayload {
  limit: number;
  offset: number;
  title: string;
  isActive?: number;
  geos: string[];
  order: {
    title: string;
    id: string;
    isActive: string;
  };
}

const initialState: BannersState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  isFetching: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  ctaTargets: [],
  isCtaLoading: false,
  processingItemId: undefined,
};

export const getBannersList = createAsyncThunk(
  "cms-banners/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.bannersList}`, data);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const createBannerGroup = createAsyncThunk<
  number,
  BannerItem,
  { rejectValue: ApiErrorPayload }
>("cms-banners/create-group", async (data, { rejectWithValue }) => {
  try {
    const result = await instance.post(`${apiRoutes.bannersCreateGroup}`, {
      ...data,
      isActive: data.isActive ? 1 : 0,
    });

    return result.data.id;
  } catch (error: any) {
    const apiError = error?.data?.error;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updateBannerGroup = createAsyncThunk<
  number,
  BannerItem,
  { rejectValue: ApiErrorPayload }
>("cms-banners/update-group", async (data, { rejectWithValue }) => {
  try {
    await instance.put(`${apiRoutes.bannersUpdateGroup}/${data.id}`, {
      ...data,
      isActive: data.isActive ? 1 : 0,
    });

    return 1;
  } catch (error: any) {
    const apiError = error?.data?.error;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});

export const updateBannersBunch = createAsyncThunk(
  "cms-banners/update-bunch",
  async (data: BannerBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.updateBannersBunch}`, {
        ...data,
        isActive: data.isActive,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);
export const deleteBannerGroup = createAsyncThunk(
  "cms-banners/delete-group",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.bannersDeleteGroup}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);
export const deleteBanner = createAsyncThunk(
  "cms-banners/delete",
  async (id: string | number) => {
    try {
      await instance.delete(`${apiRoutes.bannersDelete}/${id}`);
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const createBanner = createAsyncThunk<
  number,
  BannerPayload,
  { rejectValue: ApiErrorPayload }
>("cms-banners/create", async (payload, { rejectWithValue }) => {
  const data = new FormData();

  if (payload.imageBig && payload.imageBig instanceof File) {
    const safeName = sanitizeFileName(payload.imageBig.name);
    const safeFile = new File([payload.imageBig], safeName, {
      type: payload.imageBig.type,
    });
    data.append("imageBig", safeFile);
  }

  if (payload.imageSmall && payload.imageSmall instanceof File) {
    const safeName = sanitizeFileName(payload.imageSmall.name);
    const safeFile = new File([payload.imageSmall], safeName, {
      type: payload.imageSmall.type,
    });
    data.append("imageSmall", safeFile);
  }
  data.append("geo", payload.geo as string);
  data.append("ctaType", payload.ctaType ?? "none");

  if (payload.ctaActionType) {
    data.append("cta[actionType]", payload.ctaActionType);

    if (payload.ctaInstanceId !== undefined) {
      data.append("cta[instanceId]", String(payload.ctaInstanceId));
    }

    if (payload.ctaProviderSlug) {
      data.append("cta[providerSlug]", payload.ctaProviderSlug);
    }

    if (payload.ctaSlug) {
      data.append("cta[slug]", payload.ctaSlug);
    }
  }

  data.append("bannerGroupId", payload.bannerGroupId as string);

  const normalizedLocales = payload.locales?.filter(
    (el) => el && (el.title || el.content || el.cta || el.note),
  );

  normalizedLocales?.forEach((el, idx) => {
    if (el?.locale) data.append(`texts[${idx}][locale]`, el.locale);
    if (el?.title) data.append(`texts[${idx}][title]`, el.title);
    if (el?.content) data.append(`texts[${idx}][content]`, el.content);
    if (el?.cta) data.append(`texts[${idx}][cta]`, el.cta);
    if (el?.note) data.append(`texts[${idx}][note]`, el.note);
  });

  try {
    await instance.post(`${apiRoutes.bannersCreate}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return 1;
  } catch (error: any) {
    const apiError = error?.data?.error;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});
export const updateBanner = createAsyncThunk<
  number,
  BannerPayload,
  { rejectValue: ApiErrorPayload }
>("cms-banners/update", async (payload, { rejectWithValue }) => {
  const data = new FormData();

  if (payload.imageBig && payload.imageBig instanceof File) {
    const safeName = sanitizeFileName(payload.imageBig.name);
    const safeFile = new File([payload.imageBig], safeName, {
      type: payload.imageBig.type,
    });
    data.append("imageBig", safeFile);
  }

  if (payload.imageSmall && payload.imageSmall instanceof File) {
    const safeName = sanitizeFileName(payload.imageSmall.name);
    const safeFile = new File([payload.imageSmall], safeName, {
      type: payload.imageSmall.type,
    });
    data.append("imageSmall", safeFile);
  }

  data.append("geo", payload.geo as string);
  data.append("ctaType", payload.ctaType ?? "none");

  if (payload.ctaActionType) {
    data.append("cta[actionType]", payload.ctaActionType);

    if (payload.ctaInstanceId !== undefined) {
      data.append("cta[instanceId]", String(payload.ctaInstanceId));
    }

    if (payload.ctaProviderSlug) {
      data.append("cta[providerSlug]", payload.ctaProviderSlug);
    }

    if (payload.ctaSlug) {
      data.append("cta[slug]", payload.ctaSlug);
    }
  }

  data.append("bannerGroupId", payload.bannerGroupId as string);

  payload.locales?.forEach((el, idx) => {
    if (el?.locale) data.append(`texts[${idx}][locale]`, el.locale);
    if (el?.title) data.append(`texts[${idx}][title]`, el.title);
    if (el?.content) data.append(`texts[${idx}][content]`, el.content);
    if (el?.cta) data.append(`texts[${idx}][cta]`, el.cta);
    if (el?.note) data.append(`texts[${idx}][note]`, el.note);
  });

  try {
    await instance.put(`${apiRoutes.bannersUpdate}/${payload.id}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return 1;
  } catch (error: any) {
    const apiError = error?.data?.error;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});
export const getBannerGroupItem = createAsyncThunk(
  "cms-banners/read",
  async (id: string | number) => {
    try {
      const result = await instance.get(`${apiRoutes.bannersGroupItem}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getCtaEntities = createAsyncThunk(
  "cms-banners/get-cta-entities",
  async ({
    actionType,
    country,
    search,
  }: {
    actionType: string;
    country: string;
    search?: string;
  }) => {
    let response;

    switch (actionType) {
      case "tournament":
        response = await instance.post(apiRoutes.tournamentsList, {
          country: [country],
          title: search ?? undefined,
        });
        break;

      case "bonus":
        response = await instance.post(apiRoutes.bonusesList, {
          country: [country],
          title: search ?? undefined,
        });
        break;

      case "promo":
        response = await instance.post(apiRoutes.promoList, {
          country: [country],
          title: search ?? undefined,
        });
        break;

      case "game":
        response = await instance.post(apiRoutes.gamesList, {
          limit: 100,
          offset: 0,
          isActive: 1,
          gameTitle: search ?? undefined,
          order: {
            providerTitle: "desc",
          },
        });
        break;
      case "tag":
        response = await instance.post(apiRoutes.tagsList, {
          title: search ?? undefined,
        });
        break;

      case "collection":
        response = await instance.post(apiRoutes.collectionsList, {
          title: search ?? undefined,
        });
        break;

      default:
        return [];
    }

    return response.data?.items ?? response.data?.data?.items ?? [];
  },
);

export const cmsBannersSlice = createSlice({
  name: "cms-banners",
  initialState,
  reducers: {
    handlePaginate: (state: BannersState, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleUpdateFieldsGroup: (
      state: BannersState,
      action: PayloadAction<BannerItem>,
    ) => {
      state.payloadGroup = { ...state.payloadGroup, ...action.payload };
    },
    handleUpdateFields: (
      state: BannersState,
      action: PayloadAction<BannerPayload>,
    ) => {
      if (!state.payload) {
        state.payload = [action.payload];
        return;
      }

      const index = state.payload.findIndex(
        (item) =>
          item.innerId === action.payload.innerId &&
          item.geo === action.payload.geo,
      );

      if (index !== -1) {
        state.payload[index] = { ...state.payload[index], ...action.payload };
      } else {
        state.payload.push(action.payload);
      }
    },
    handleClearFields: (state: BannersState) => {
      state.payload = undefined;
      state.payloadGroup = undefined;
      state.isItemFetched = false;
      state.isFetching = false;
    },
    handleSelectItem: (state: BannersState, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: BannersState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id ?? 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getBannersList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createBannerGroup.fulfilled, (state) => {
      state.isListFetched = false;
    });
    builder.addCase(createBanner.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(createBanner.fulfilled, (state) => {
      state.isListFetched = false;
      state.isFetching = false;
    });
    builder.addCase(createBanner.rejected, (state) => {
      state.isFetching = false;
    });
    builder.addCase(updateBannerGroup.pending, (state, action) => {
      state.isFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateBannerGroup.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? {
              ...item,
              ...Object.fromEntries(
                Object.keys(item)
                  .filter(
                    (field) =>
                      field in action.meta.arg &&
                      action.meta.arg[field as keyof BannerItem] !== undefined,
                  )
                  .map((field) => [
                    field,
                    action.meta.arg[field as keyof BannerItem],
                  ]),
              ),
            }
          : item,
      );
      state.isFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateBannerGroup.rejected, (state) => {
      state.isFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateBannersBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
    builder.addCase(deleteBannerGroup.pending, (state, action) => {
      state.isFetching = true;
      state.processingItemId = +action.meta.arg || 0;
    });

    builder.addCase(deleteBannerGroup.fulfilled, (state) => {
      state.isListFetched = false;
      state.isFetching = false;
      state.processingItemId = undefined;
    });

    builder.addCase(deleteBannerGroup.rejected, (state) => {
      state.isFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(getBannerGroupItem.pending, (state) => {
      state.isItemFetched = false;
    });
    builder.addCase(
      getBannerGroupItem.fulfilled,
      (state, action: PayloadAction<BannerItem>) => {
        state.isItemFetched = true;
        state.payloadGroup = action.payload;
        const geos: any = {};
        // @ts-ignore
        const result: BannerPayload[] =
          action.payload.banners?.map((el) => {
            if (geos[el.geo]) {
              geos[el.geo] = geos[el.geo] + 1;
            } else {
              geos[el.geo] = 1;
            }
            return {
              imageBig: el.imageBig,
              imageSmall: el.imageSmall,
              geo: el.geo,
              ctaType: el.ctaType,
              ctaActionType: el.cta?.actionType,
              ctaInstanceId: el.cta?.instanceId,
              ctaSlug: el.cta?.slug,
              innerId: geos[el.geo] ? `Item${geos[el.geo]}` : `Item1`,
              locales: el.texts?.map((item) => item) ?? [],
              id: el.id,
              ctaProviderSlug: el.cta?.providerSlug,
            };
          }) ?? [];
        state.payload = result ?? undefined;
      },
    );
    builder.addCase(updateBanner.pending, (state, action) => {
      state.isFetching = true;
      state.processingItemId = Number(action.meta.arg.bannerGroupId);
    });
    builder.addCase(updateBanner.fulfilled, (state, action) => {
      const banner = action.meta.arg;
      state.list = state.list.map((group) => {
        if (group.id !== Number(banner.bannerGroupId) || !group.banners) {
          return group;
        }
        return {
          ...group,
          banners: group.banners.map((item) =>
            String(item.id) === banner.id
              ? {
                  ...item,
                  imageBig:
                    banner.imageBig instanceof File
                      ? item.imageBig
                      : String(banner.imageBig),
                  imageSmall:
                    banner.imageSmall instanceof File
                      ? item.imageSmall
                      : String(banner.imageSmall),
                }
              : item,
          ),
        };
      });
      state.isFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateBanner.rejected, (state) => {
      state.isFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deleteBanner.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(deleteBanner.fulfilled, (state) => {
      state.isListFetched = false;
      state.isFetching = false;
    });
    builder.addCase(deleteBanner.rejected, (state) => {
      state.isFetching = false;
    });
    builder.addCase(getCtaEntities.pending, (state) => {
      state.isCtaLoading = true;
    });

    builder.addCase(getCtaEntities.fulfilled, (state, action) => {
      state.isCtaLoading = false;

      state.ctaTargets = action.payload.map((item: any) => ({
        label: item.title || item.name,
        value: item.id,
        slug: item.slug,
        providerSlug: item.provider?.slug,
        img: item.imageSmall || item.image || NoImage,
      }));
    });

    builder.addCase(getCtaEntities.rejected, (state) => {
      state.isCtaLoading = false;
      state.ctaTargets = [];
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleUpdateFields,
  handleClearFields,
  handleUpdateFieldsGroup,
} = cmsBannersSlice.actions;
